import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Modal, Alert } from "react-bootstrap";
import {
  createStockAdjustment,
  updateStockAdjustment,
  fetchStockAdjustmentById,
  clearError,
  clearSuccess,
} from "../../redux/slices/inventorySlice";
import { fetchProducts } from "../../redux/slices/productSlice";
import { toast } from "react-toastify";

const StockAdjustmentModal = ({
  show,
  handleClose,
  stockAdjustment = null,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!stockAdjustment;

  const {
    isCreatingStockAdjustment,
    isUpdatingStockAdjustment,
    error,
    success,
    currentStockAdjustment,
  } = useSelector((state) => state.inventory);

  const { products = [] } = useSelector((state) => state.products);

  const [formData, setFormData] = useState({
    product_id: "",
    adjustment_type: "increase",
    quantity: 1,
    reason: "manual",
    date: new Date().toISOString().split("T")[0],
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load products when modal opens
  useEffect(() => {
    if (show) {
      dispatch(fetchProducts());
    }
  }, [show, dispatch]);

  // Load stock adjustment data if in edit mode
  useEffect(() => {
    if (isEditMode && stockAdjustment && show) {
      if (
        !currentStockAdjustment ||
        currentStockAdjustment.id !== stockAdjustment.id
      ) {
        dispatch(fetchStockAdjustmentById(stockAdjustment.id));
      }
    }
  }, [dispatch, stockAdjustment, isEditMode, show, currentStockAdjustment]);

  // Update form data when currentStockAdjustment changes
  useEffect(() => {
    if (
      isEditMode &&
      currentStockAdjustment &&
      currentStockAdjustment.id === stockAdjustment?.id
    ) {
      setFormData({
        product_id: currentStockAdjustment.product_id || "",
        adjustment_type: currentStockAdjustment.adjustment_type || "increase",
        quantity: currentStockAdjustment.quantity || 1,
        reason: currentStockAdjustment.reason || "manual",
        date:
          currentStockAdjustment.date || new Date().toISOString().split("T")[0],
      });
    }
  }, [currentStockAdjustment, isEditMode, stockAdjustment]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      if (!isEditMode) {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [show, isEditMode]);

  const resetForm = () => {
    setFormData({
      product_id: "",
      adjustment_type: "increase",
      quantity: 1,
      reason: "manual",
      date: new Date().toISOString().split("T")[0],
    });
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.product_id) {
      errors.product_id = "Product is required";
    }

    if (!formData.quantity || formData.quantity <= 0) {
      errors.quantity = "Valid quantity is required";
    }

    if (!formData.reason) {
      errors.reason = "Reason is required";
    }

    if (!formData.date) {
      errors.date = "Date is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      setIsSubmitting(true);

      const submitData = {
        ...formData,
        quantity: parseInt(formData.quantity),
      };

      if (isEditMode) {
        await dispatch(
          updateStockAdjustment({
            id: stockAdjustment.id,
            adjustmentData: submitData,
          })
        ).unwrap();
        toast.success("Stock adjustment updated successfully!");
      } else {
        await dispatch(createStockAdjustment(submitData)).unwrap();
        toast.success("Stock adjustment created successfully!");
      }

      setTimeout(() => {
        handleModalClose();
      }, 1500);
    } catch (err) {
      console.error("Stock adjustment operation failed:", err);
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    dispatch(clearError());
    dispatch(clearSuccess());
    handleClose();
  };

  const isProcessing =
    isCreatingStockAdjustment || isUpdatingStockAdjustment || isSubmitting;

  const validReasons = [
    { value: "damage", label: "Damage" },
    { value: "expired", label: "Expired" },
    { value: "manual", label: "Manual" },
    { value: "return", label: "Return" },
    { value: "found", label: "Found" },
    { value: "theft", label: "Theft" },
    { value: "correction", label: "Correction" },
    { value: "purchase", label: "Purchase" },
    { value: "purchase_cancellation", label: "Purchase Cancellation" },
  ];

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Stock Adjustment" : "Create Stock Adjustment"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Product <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.product_id}
                  disabled={isProcessing}
                >
                  <option value="">Select a product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} (Stock: {product.stock_quantity || 0})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.product_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Adjustment Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="adjustment_type"
                  value={formData.adjustment_type}
                  onChange={handleInputChange}
                  disabled={isProcessing}
                >
                  <option value="increase">Increase Stock</option>
                  <option value="decrease">Decrease Stock</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Quantity <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  name="quantity"
                  placeholder="Enter quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.quantity}
                  disabled={isProcessing}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.quantity}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {formData.adjustment_type === "increase"
                    ? "Adding"
                    : "Removing"}{" "}
                  {formData.quantity} units
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.date}
                  disabled={isProcessing}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              isInvalid={!!validationErrors.reason}
              disabled={isProcessing}
            >
              {validReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.reason}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Current Stock Info */}
          {formData.product_id && (
            <Alert variant="info" className="mb-3">
              <strong>Current Stock:</strong>{" "}
              {products.find((p) => p.id === formData.product_id)
                ?.stock_quantity || 0}{" "}
              units
              <br />
              <strong>After Adjustment:</strong>{" "}
              {(() => {
                const currentStock =
                  products.find((p) => p.id === formData.product_id)
                    ?.stock_quantity || 0;
                const adjustment =
                  formData.adjustment_type === "increase"
                    ? formData.quantity
                    : -formData.quantity;
                return currentStock + adjustment;
              })()}{" "}
              units
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isProcessing}>
            {isProcessing
              ? "Processing..."
              : isEditMode
              ? "Update Stock Adjustment"
              : "Create Stock Adjustment"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StockAdjustmentModal;
